import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { deals } from '@/mocks/deals';
import { articles } from '@/mocks/articles';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q2ruee3oq4jqumtfm0';

export default function Home() {
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [subscribeMsg, setSubscribeMsg] = useState('');

  const hotDeals = deals.filter(d => d.isHot).slice(0, 3);
  const newDeals = deals.filter(d => d.isNew).slice(0, 4);
  const featuredArticles = articles.filter(a => a.isFeatured);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string | null;
    if (honeypot && honeypot.trim()) {
      setSubscribeStatus('success');
      setSubscribeMsg('订阅成功！感谢您的关注。');
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && value) {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText) as Record<string, unknown>; } catch { parsed = null; }

      if (response.ok && parsed && parsed.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMsg('订阅成功！感谢您的关注。');
        form.reset();
      } else {
        const meta = (parsed?.meta as Record<string, string>) || {};
        const serverMsg = meta?.message || (parsed?.message as string) || '订阅失败，请稍后重试';
        setSubscribeStatus('error');
        setSubscribeMsg(serverMsg);
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMsg('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="福利吧 - 每日精选福利汇聚，好物好价不错过"
        description="福利吧是专为爱好优惠的用户打造的福利聚合平台，每日精选限时折扣、免费领取、优惠券及隐藏福利资源，帮你省钱又省心。"
        keywords="福利吧,每日福利精选,限时优惠活动,免费领取福利,优惠券汇总,隐藏福利资源,省钱攻略推荐"
        canonicalUrl="https://fuliba.com/"
        ogType="website"
        breadcrumbs={[
          { name: '首页', url: 'https://fuliba.com/' },
        ]}
      />

      <Navbar />

      <main>
        <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20warm%20orange%20and%20coral%20abstract%20geometric%20background%20with%20soft%20gradients%20and%20floating%20circular%20shapes%2C%20modern%20minimalist%20design%2C%20clean%20composition%20with%20ample%20negative%20space%2C%20bright%20and%20energetic%20atmosphere%2C%20suitable%20for%20text%20overlay%2C%20no%20text%20or%20lettering%2C%20abstract%20art%20style%20with%20smooth%20transitions&width=1800&height=900&seq=hero-bg-fuliba&orientation=landscape"
            alt="福利吧"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

          <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
              <div className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></div>
              <span className="text-background-50 text-sm font-medium">
                每日更新 · 实时同步 · 全网精选
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold font-heading text-background-50 mb-6 leading-tight">
              每日精选福利<span className="text-primary-400">汇聚</span>
              <br />
              好物好价<span className="text-secondary-300">不错过</span>
            </h1>

            <p className="text-background-50/80 text-lg md:text-xl mb-10 max-w-xl mx-auto leading-relaxed">
              限时折扣、免费领取、优惠券、隐藏福利 — 一站式聚合，
              帮你省钱又省心
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/flash-sale"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all text-base whitespace-nowrap cursor-pointer"
              >
                查看限时秒杀
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/category/free"
                className="w-full sm:w-auto px-8 py-3.5 bg-background-50/15 hover:bg-background-50/25 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full transition-all text-base whitespace-nowrap cursor-pointer"
              >
                免费福利领取
                <i className="ri-gift-line ml-2"></i>
              </Link>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 mt-12">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-accent-400 font-heading">10,000+</div>
                <div className="text-background-50/70 text-sm mt-1">每日更新福利</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-secondary-300 font-heading">500万+</div>
                <div className="text-background-50/70 text-sm mt-1">累计省钱金额</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-400 font-heading">50万+</div>
                <div className="text-background-50/70 text-sm mt-1">活跃省钱用户</div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-100 text-foreground-700 hover:text-primary-700 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门福利
                </h2>
                <p className="text-foreground-600 text-sm mt-2">大家都在抢的超值福利，手慢无</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {hotDeals.map((deal) => (
                <Link
                  key={deal.id}
                  to={`/category/${deal.category}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={deal.image}
                      alt={deal.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                        {deal.discount}
                      </span>
                    </div>
                    {deal.isFree && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                          免费
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-foreground-900 text-base line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {deal.title}
                    </h3>
                    <p className="text-foreground-600 text-sm mt-1.5 line-clamp-2">{deal.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-bold text-primary-600">
                          {deal.currentPrice === 0 ? '免费' : `¥${deal.currentPrice}`}
                        </span>
                        {deal.originalPrice > 0 && (
                          <span className="text-foreground-400 text-sm line-through">¥{deal.originalPrice}</span>
                        )}
                      </div>
                      <span className="text-foreground-500 text-xs">截止 {deal.expiryDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  <i className="ri-sparkling-line text-accent-500 mr-2"></i>
                  最新福利
                </h2>
                <p className="text-foreground-600 text-sm mt-2">刚刚上新的福利，新鲜出炉别错过</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {newDeals.map((deal) => (
                <Link
                  key={deal.id}
                  to={`/category/${deal.category}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-accent-200 transition-all cursor-pointer"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={deal.image}
                      alt={deal.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                        NEW
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-foreground-900 text-sm line-clamp-2 group-hover:text-accent-600 transition-colors">
                      {deal.title}
                    </h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-base font-bold text-accent-600">
                        {deal.currentPrice === 0 ? '免费' : `¥${deal.currentPrice}`}
                      </span>
                      <span className="text-foreground-400 text-xs line-through">
                        {deal.originalPrice > 0 ? `¥${deal.originalPrice}` : ''}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                省钱攻略精选
              </h2>
              <p className="text-foreground-600 text-sm mt-2">掌握省钱技巧，让每一笔消费都物超所值</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col sm:flex-row bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="sm:w-56 h-44 sm:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                        {article.category}
                      </span>
                      <h3 className="font-bold text-foreground-900 text-base mt-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-foreground-600 text-sm mt-1.5 line-clamp-2">{article.excerpt}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-foreground-500 text-xs">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {article.readTime}
                      </span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20gradient%20background%20with%20coral%20and%20peach%20tones%2C%20soft%20abstract%20flowing%20shapes%2C%20clean%20modern%20design%2C%20energetic%20yet%20elegant%20atmosphere%2C%20smooth%20color%20transitions%2C%20minimal%20abstract%20art&width=1800&height=600&seq=cta-bg-fuliba&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-accent-600/80"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-4">
              每天都有新福利，你准备好了吗？
            </h2>
            <p className="text-background-50/80 text-base md:text-lg mb-8 max-w-lg mx-auto">
              订阅福利吧新闻简报，每日推送最新福利资讯，第一时间掌握省钱机会
            </p>
            <div className="max-w-md mx-auto">
              <form onSubmit={handleSubscribe} data-readdy-form="true">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="spam-field"
                />
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    name="email"
                    placeholder="输入你的邮箱地址"
                    required
                    className="flex-1 px-5 py-3 rounded-full bg-background-50 text-foreground-900 placeholder:text-foreground-400 text-sm border border-transparent focus:outline-none focus:ring-2 focus:ring-background-50/50"
                  />
                  <button
                    type="submit"
                    className="px-8 py-3 rounded-full bg-foreground-950 hover:bg-foreground-900 text-background-50 font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    立即订阅
                  </button>
                </div>
              </form>
              {subscribeStatus !== 'idle' && (
                <p className={`mt-3 text-sm ${subscribeStatus === 'success' ? 'text-background-50' : 'text-secondary-200'}`}>
                  {subscribeMsg}
                </p>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '福利吧 - 每日精选福利汇聚',
        description: '福利吧是专为爱好优惠的用户打造的福利聚合平台，每日精选限时折扣、免费领取、优惠券及隐藏福利资源。',
        url: 'https://fuliba.com/',
        isPartOf: {
          '@type': 'WebSite',
          name: '福利吧',
          url: 'https://fuliba.com',
        },
      })}} />
    </>
  );
}